import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@icc/shared';
import { SalesDocument, SalesDocumentType, OfferValidity, PriceVersion } from '@icc/b2b/shared';
import { PriceVersionService } from '../price-version-selector/price-version.service';
import { parseDate } from '@icc/shared/helpers';

@Injectable({
    providedIn: 'root',
})
export class OfferValidityService {
    offerValidities: OfferValidity[] = [];

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private priceVersionService: PriceVersionService
    ) {}

    public static calculateValidity(
        documentPriceVersion: { expiredDate: null | Date; effectiveDate: null | Date },
        validityDate: Date,
        offerDatePriority: number,
        isDocumentConfirmed = false,
        hasDocumentOnlyCustomValuatedPositions = false,
        ignorePriceVersionValidity = false
    ) {
        let offerValidity = parseDate(validityDate);
        if (
            documentPriceVersion?.expiredDate &&
            offerValidity &&
            !hasDocumentOnlyCustomValuatedPositions &&
            !ignorePriceVersionValidity &&
            ((offerDatePriority === 1 &&
                new Date(documentPriceVersion?.expiredDate) < offerValidity) ||
                (offerDatePriority === 2 && !isDocumentConfirmed))
        ) {
            offerValidity = new Date(documentPriceVersion?.expiredDate);
        } else if (!offerValidity) {
            if (documentPriceVersion?.expiredDate) {
                offerValidity = new Date(documentPriceVersion?.expiredDate);
            } else {
                const maxTimestamp = 8640000000000000;
                offerValidity = new Date(maxTimestamp);
            }
        }
        return offerValidity;
    }

    public async loadValidityDates() {
       this.offerValidities = this.configService.config?.offerValidities;
    }

    public getValidities() {
        return this.offerValidities;
    }

    public getCurrentOfferValidity(document: SalesDocument) {
        const offerValidities: OfferValidity[] = this.getValidities();
        if (offerValidities.length) {
            const currentOfferValidity = offerValidities.find(
                (el) => el.currency === document.priceCurrency
            );
            return currentOfferValidity;
        }
    }

    static getValidDate(
        document: SalesDocument,
        documentPriceVersion: PriceVersion,
        offerDatePriority: number
    ) {
        let showValidityInfo = false;
        let showOfferValidityWarning = false;
        let invalidOffer = false;
        const visibleValidDate = OfferValidityService.calculateValidity(
            documentPriceVersion,
            document?.validityDate,
            offerDatePriority,
            document?.isConfirmed,
            document?.hasOnlyCustomValuatedPositions,
            document?.ignorePriceVersionValidity,
        );

        if (
            (document?.salesDocumentTypeId === SalesDocumentType.Quotation &&
                !document.isEmpty &&
                !document.isOrderPlaced &&
                !document.isCanceled) ||
            (document?.salesDocumentTypeId === SalesDocumentType.Order &&
                !document?.isOrderPlaced &&
                !document?.isCanceled &&
                !document?.isAnnexed &&
                !document.isConfirmedOnlySalesPositions &&
                !document.partialExternalSource &&
                document.externalSource !== 'ERP')
        ) {
            if (document?.validityDate && visibleValidDate) {
                showValidityInfo = true;
                const diffDays = Math.round(
                    (visibleValidDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                );
                if (
                    diffDays <= 2 &&
                    diffDays >= 0 &&
                    visibleValidDate.getTime() > new Date().getTime()
                ) {
                    showOfferValidityWarning = true;
                }
                if (visibleValidDate.getTime() < new Date().getTime()) {
                    invalidOffer = true;
                }
            } else {
                showValidityInfo = false;
                if (visibleValidDate.getTime() < new Date().getTime()) {
                    invalidOffer = true;
                }
            }
        }
        return {
            showValidityInfo,
            showOfferValidityWarning,
            invalidOffer,
            visibleValidDate,
        };
    }

    getValidDate(document: SalesDocument) {
        const documentPriceVersion = this.priceVersionService.getPriceVersion(
            document?.priceVersionId
        );
        return OfferValidityService.getValidDate(
            document,
            documentPriceVersion,
            this.configService?.user?.offerDatePriority
        );
    }
}
